// src/pages/enterprise/EnterpriseProgramTab.js
//
// Read-only Activity Ledger. Audit view of what each company has run on
// the platform on their own. Their programs, the agents that executed
// them, and every job fired. We observe here; we do not edit their data.
//
// No write surfaces: no Add, no Edit, no Run. Clicking a program card or
// a single job-history row opens ProgramDetailModal, which is also fully
// read-only.
//
// The AI-generated summary lives in program.summary; today it is a
// pre-written dummy string. When a DB-cached LLM summary lands, it drops
// in at the same key path with no UI change.

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';

import { THEME } from '../enterprise-ai/genesisData';
import { InfoIcon, InfoIconFor } from '../../components/common/InfoIcon';
import Modal from '../../components/common/Modal';
import { programsForSlug } from './programLedger.dummy';

const PAGE_TIP =
  'Read-only audit view of what this company has run on the platform on their own. Their programs, the agents that executed them, and every job fired. We observe here; we do not edit their data.';

const SUMMARY_FALLBACK =
  'No summary yet. AI-generated summary will appear here once the corpus runs over this program.';

const outlinedBtnSx = {
  color: THEME.textSecondary,
  borderColor: THEME.borderStrong,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '12px',
  '&:hover': { borderColor: THEME.mint, color: THEME.mint, background: THEME.mintFaint },
};

const fieldLabelSx = {
  fontSize: '10px',
  color: THEME.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  mb: 0.5,
};

const STATUS_COLOR = {
  active: THEME.mint,
  paused: THEME.amber,
  planning: THEME.blue,
};

const JOB_STATUS_COLOR = {
  completed: THEME.mint,
  pending: THEME.amber,
  failed: THEME.pink,
};

const formatEmployees = (n) => {
  if (n == null || n === 0) return 'tbd';
  return typeof n === 'number' ? n.toLocaleString('en-US') : String(n);
};

// ----- helpers -----

const Stat = ({ label, value }) => (
  <Box sx={{ minWidth: 100 }}>
    <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: THEME.textPrimary, mt: 0.25 }}>{value}</Typography>
  </Box>
);

const StatusChip = ({ status }) => (
  <Box
    component="span"
    sx={{
      background: 'rgba(255,255,255,0.06)',
      color: STATUS_COLOR[status] || THEME.textMuted,
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      px: '8px',
      py: '3px',
      borderRadius: '4px',
      lineHeight: 1,
    }}
  >
    {status}
  </Box>
);

// ----- per-program card (read-only, clickable) -----

const ProgramCard = ({ program, onOpenProgram, onOpenJob }) => {
  const [historyOpen, setHistoryOpen] = useState(false);

  const stop = (e) => e.stopPropagation();

  return (
    <Box
      onClick={() => onOpenProgram(program)}
      sx={{
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '12px',
        p: 2.5,
        mb: 2,
        cursor: 'pointer',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: THEME.mint },
      }}
    >
      {/* header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLOR[program.status] || THEME.textMuted, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{program.name}</Typography>
        <StatusChip status={program.status} />
        <InfoIconFor label={program.status} />
        <InfoIcon text={program.summary || SUMMARY_FALLBACK} />
      </Box>

      {/* meta row */}
      <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2 }}>
        {program.category} . {program.cadence || 'cadence tbd'} . {program.reach.offices} offices . {formatEmployees(program.reach.employees)} employees . started {program.startedAt}
      </Typography>

      {/* agents row */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Agents</Typography>
        {program.agents.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textFaint, fontStyle: 'italic' }}>
            No agents recorded.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {program.agents.map((a) => (
              <Box
                key={a.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: '4px 8px',
                  borderRadius: '6px',
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${THEME.border}`,
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{a.name}</Typography>
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>. {a.role}</Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: a.status === 'live' ? THEME.mint : THEME.amber,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {a.status}
                </Typography>
                <InfoIconFor label={a.status} />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* attendance metrics */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Attendance</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
          <Stat label="Avg" value={program.attendance.avgAttendance} />
          <Stat label="Peak" value={program.attendance.peakAttendance} />
          <Stat
            label="Satisfaction"
            value={program.attendance.satisfactionScore != null ? `${program.attendance.satisfactionScore}/5` : 'n/a'}
          />
          <Stat label="Monthly active" value={program.attendance.monthlyActiveRate} />
        </Box>
      </Box>

      {/* history (expandable; toggle does not bubble to card click) */}
      <Box onClick={stop}>
        <Box
          component="button"
          onClick={() => setHistoryOpen((o) => !o)}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: THEME.textMuted,
            '&:hover': { color: THEME.textSecondary },
          }}
        >
          <HistoryIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: '12px' }}>
            History ({program.history.length}) {historyOpen ? '- hide' : '- show'}
          </Typography>
        </Box>
        {historyOpen && (
          <Box sx={{ mt: 1 }}>
            {program.history.length === 0 ? (
              <Typography sx={{ fontSize: '11px', color: THEME.textFaint, fontStyle: 'italic' }}>
                No jobs run yet.
              </Typography>
            ) : (
              program.history.slice(0, 5).map((j) => (
                <Box
                  key={j.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenJob(program, j.id);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    borderBottom: `1px solid ${THEME.border}`,
                    cursor: 'pointer',
                    '&:hover': { background: 'rgba(255,255,255,0.02)' },
                  }}
                >
                  <Typography sx={{ fontSize: '11px', color: THEME.textPrimary, minWidth: 110 }}>{j.when}</Typography>
                  <Typography sx={{ fontSize: '11px', color: THEME.textMuted, minWidth: 90 }}>{j.kind}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: JOB_STATUS_COLOR[j.status] || THEME.textMuted,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      {j.status}
                    </Typography>
                    <InfoIconFor label={j.status} />
                  </Box>
                  <Typography sx={{ fontSize: '11px', color: THEME.textMuted, flex: 1 }}>{j.notes}</Typography>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ----- read-only detail modal -----

const ProgramDetailModal = ({ open, onClose, detail }) => {
  if (!detail) return null;
  const { program, jobId } = detail;
  const focusedJob = jobId ? program.history.find((j) => j.id === jobId) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={program.name}
      subtitle={`${program.category} . ${program.cadence || 'cadence tbd'}`}
      size="lg"
      footer={
        <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <StatusChip status={program.status} />
        <InfoIconFor label={program.status} />
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>started {program.startedAt}</Typography>
      </Box>

      {focusedJob && (
        <Box
          sx={{
            background: THEME.mintFaint,
            border: `1px solid ${THEME.mint}`,
            borderRadius: '8px',
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={fieldLabelSx}>Focused job</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary }}>{focusedJob.when}</Typography>
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>. {focusedJob.kind}</Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: JOB_STATUS_COLOR[focusedJob.status] || THEME.textMuted,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              {focusedJob.status}
            </Typography>
            <InfoIconFor label={focusedJob.status} />
          </Box>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
            {focusedJob.notes}
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Summary</Typography>
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          {program.summary || SUMMARY_FALLBACK}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Reach</Typography>
        <Typography sx={{ fontSize: '13px', color: THEME.textPrimary }}>
          {program.reach.offices} offices . {formatEmployees(program.reach.employees)} employees
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Agents that touched this program</Typography>
        {program.agents.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textFaint, fontStyle: 'italic' }}>
            No agents recorded.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
            {program.agents.map((a) => (
              <Box key={a.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{a.name}</Typography>
                <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>. {a.role}</Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: a.status === 'live' ? THEME.mint : THEME.amber,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {a.status}
                </Typography>
                <InfoIconFor label={a.status} />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Attendance</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mt: 0.5 }}>
          <Stat label="Avg" value={program.attendance.avgAttendance} />
          <Stat label="Peak" value={program.attendance.peakAttendance} />
          <Stat
            label="Satisfaction"
            value={program.attendance.satisfactionScore != null ? `${program.attendance.satisfactionScore}/5` : 'n/a'}
          />
          <Stat label="Monthly active" value={program.attendance.monthlyActiveRate} />
        </Box>
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Job history ({program.history.length})</Typography>
        {program.history.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textFaint, fontStyle: 'italic' }}>
            No jobs run yet.
          </Typography>
        ) : (
          <Box sx={{ mt: 0.5 }}>
            {program.history.map((j) => {
              const focused = jobId && j.id === jobId;
              return (
                <Box
                  key={j.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1,
                    borderBottom: `1px solid ${THEME.border}`,
                    borderLeft: focused ? `2px solid ${THEME.mint}` : '2px solid transparent',
                    pl: focused ? 1 : 0,
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: THEME.textPrimary, minWidth: 110 }}>{j.when}</Typography>
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted, minWidth: 90 }}>{j.kind}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: JOB_STATUS_COLOR[j.status] || THEME.textMuted,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      {j.status}
                    </Typography>
                    <InfoIconFor label={j.status} />
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, flex: 1, lineHeight: 1.5 }}>
                    {j.notes}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mt: 2, fontStyle: 'italic' }}>
        Read-only audit view. Nothing here is editable from inside United.
      </Typography>
    </Modal>
  );
};

// ----- main -----

export default function EnterpriseProgramTab({ orgSlug }) {
  const [programs, setPrograms] = useState(() => programsForSlug(orgSlug));
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setPrograms(programsForSlug(orgSlug));
  }, [orgSlug]);

  const openProgram = (program) => setDetail({ program });
  const openJob = (program, jobId) => setDetail({ program, jobId });
  const closeDetail = () => setDetail(null);

  return (
    <Box sx={{ color: THEME.textPrimary }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Activity Ledger</Typography>
        <InfoIcon text={PAGE_TIP} />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
          {programs.length} program{programs.length === 1 ? '' : 's'} observed
        </Typography>
      </Box>

      {programs.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px dashed ${THEME.borderStrong}`,
            borderRadius: '8px',
          }}
        >
          <Typography sx={{ fontSize: '13px', color: THEME.textMuted }}>
            No programs observed for this enterprise yet.
          </Typography>
        </Box>
      ) : (
        programs.map((p) => (
          <ProgramCard key={p.id} program={p} onOpenProgram={openProgram} onOpenJob={openJob} />
        ))
      )}

      <ProgramDetailModal open={!!detail} onClose={closeDetail} detail={detail} />
    </Box>
  );
}
